import { useEffect, useMemo, useState } from "react";

type UseTypewriterOptions = {
  speedMs?: number;
  startDelayMs?: number;
  showCursor?: boolean; // Whether to append an underscore cursor while typing
};

export function useTypewriter(text: string, options: UseTypewriterOptions = {}) {
  const { speedMs = 40, startDelayMs = 120, showCursor = true } = options;

  const stableText = useMemo(() => text ?? "", [text]);
  const [value, setValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    let timeoutId: number | undefined;

    setValue("");
    setIsTyping(true);

    const start = () => {
      let i = 0;
      const tick = () => {
        if (isCancelled) return;
        i += 1;
        setValue(stableText.slice(0, i));

        if (i < stableText.length) {
          timeoutId = window.setTimeout(tick, speedMs);
        } else {
          setIsTyping(false);
        }
      };

      if (stableText.length > 0) {
        timeoutId = window.setTimeout(tick, speedMs);
      } else {
        setIsTyping(false);
      }
    };

    timeoutId = window.setTimeout(start, startDelayMs);

    return () => {
      isCancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [stableText, speedMs, startDelayMs]);

  // Return value with optional blinking underscore cursor while typing
  const displayValue = showCursor && isTyping ? value + "_" : value;

  return displayValue;
}
